import React, { useCallback, useEffect, useMemo } from 'react';
import { Grid, Autocomplete, TextField, Checkbox, Alert } from '@mui/material';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { FormProps } from 'util/Form';
import { defaultGridContainerProps, defaultGridItemProps } from 'util/Layout';
import { AuraAttributeOption, PreferenceQuestionView } from 'store/types/AuraAttribute';
import { SelectQuestionOption } from 'store/types/SelectOption';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

import commonStyles from 'styles/common.module.scss';
import styles from './MultiSelectQuestionFormItem.module.scss';

interface MultiSelectQuestionFormItemProps extends FormProps {
  question: PreferenceQuestionView;
}

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const MultiSelectQuestionFormItem: React.FunctionComponent<MultiSelectQuestionFormItemProps> = ({
  question,
  disabled,
}) => {
  const { fieldName, options, required = false } = useMemo(() => question, [question]);
  const { control, getValues } = useFormContext<{ [fieldName: string]: SelectQuestionOption[] }>();
  const { update } = useFieldArray({ control, name: fieldName });
  const [value, setValue] = React.useState<AuraAttributeOption[]>([]);
  const [inputValue, setInputValue] = React.useState('');
  const [showValidationError, setShowValidationError] = React.useState(false);
  const validationMessage = 'Please select one or more options.';

  const handleChange = useCallback(
    (data: AuraAttributeOption[]) => {
      setValue(data);
      const fieldValues = getValues()[fieldName];
      fieldValues.map((option, index) => {
        const isSelected = data.some((v) => v.option === option.name);
        update(index, { ...option, value: isSelected });
      });
    },
    [fieldName, getValues, update]
  );

  const validateMultiselect = useCallback(
    (items: SelectQuestionOption[] = []): boolean | string => {
      const isValid = !required || items.some((item) => !!item?.value);
      setShowValidationError(!isValid);
      return isValid;
    },
    [required]
  );

  useEffect(() => {
    const fieldValues = getValues()[fieldName];
    setValue(options.filter((option) => fieldValues.some((v) => v.name === option.option && v.value)));
  }, [getValues, fieldName, options]);

  return (
    <Grid {...defaultGridContainerProps}>
      <Grid {...defaultGridItemProps}>
        <Controller
          name={fieldName}
          control={control}
          defaultValue={[]}
          rules={{
            validate: (items: SelectQuestionOption[] = []) => validateMultiselect(items),
          }}
          render={({ field }) => (
            <Autocomplete
              value={value ?? []}
              inputValue={inputValue}
              multiple
              disableCloseOnSelect
              options={options}
              getOptionLabel={(option) => option.option}
              isOptionEqualToValue={(option, value) => option.option === value.option}
              disabled={disabled}
              classes={{ tag: styles.customTag }}
              sx={{
                '.MuiChip-deleteIcon': {
                  color: 'white !important',
                },
              }}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                  {option.option}
                </li>
              )}
              renderInput={(params) => <TextField {...params} inputRef={field.ref} required={required} />}
              onInputChange={(_event, newInputValue) => {
                setInputValue(newInputValue);
              }}
              onChange={(_event, data) => {
                handleChange(data);
              }}
            />
          )}
        />
        {showValidationError && (
          <Alert severity={'error'} className={commonStyles.alert} sx={{ marginTop: 1 }}>
            {validationMessage}
          </Alert>
        )}
      </Grid>
    </Grid>
  );
};
export default MultiSelectQuestionFormItem;
